import React, { useCallback, useMemo } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  Button,
  HStack,
  ScrollViewWithHeaderTitle,
  TextLinkifyTags,
  theme,
  TitleText,
  useResponsive,
  VStack,
} from '@sg/garnish';

import { NotFoundView } from '../../components';
import { usePersonalDataAvailability } from './PersonalData.availability';

export const PersonalDataScreen = () => {
  const isPersonalDataRequestEnabled = usePersonalDataAvailability();
  const { formatMessage } = useIntl();
  const { match, currentBreakpoint } = useResponsive();

  // ─── Links ───────────────────────────────────────────────────────────

  const links = useMemo(
    () => [
      {
        tag: 'privacy_policy',
        description: formatMessage(messages.personalDataPrivacyPolicyDescription),
        href: formatMessage(messages.personalDataPrivacyPolicyHref),
        newTab: true,
      },
      {
        tag: 'email',
        description: formatMessage(messages.personalDataEmailDescription),
        href: formatMessage(messages.personalDataEmailHref),
      },
      {
        tag: 'phone',
        description: formatMessage(messages.personalDataPhoneDescription),
        href: formatMessage(messages.personalDataPhoneHref),
      },
      {
        tag: 'opt_out',
        description: formatMessage(messages.personalDataOptOutDescription),
        href: formatMessage(messages.personalDataOptOutHref),
        newTab: true,
      },
    ],
    [formatMessage],
  );

  // ─── Navigation Callbacks ───────────────────────────────────────

  const { handleAccessRequest, handleDeleteRequest } = usePersonalDataRequestNavigation();

  // ─── Responsive Styles ──────────────────────────────────────────

  const Stack = currentBreakpoint.isXS ? VStack : HStack;
  const contentStyle = match([styles.contentSM, styles.contentLG]);
  const titleStyle = match([styles.titleSM, styles.titleLG]);

  // ─── Not Found View When Disabled ────────────────────────────────────

  if (!isPersonalDataRequestEnabled) {
    return <NotFoundView />;
  }

  return (
    <ScrollViewWithHeaderTitle>
      <View style={contentStyle}>
        <TitleText style={titleStyle} sizeMatch={['32', '48']}>
          {formatMessage(messages.personalDataTitle)}
        </TitleText>
        <BodyText sizeMatch={['14', '16']}>
          <TextLinkifyTags links={links}>
            {formatMessage(messages.personalDataBody, {
              privacy_policy: '{privacy_policy}',
              opt_out: '{opt_out}',
              phone: '{phone}',
              email: '{email}',
            })}
          </TextLinkifyTags>
        </BodyText>
        <Stack style={styles.buttons} itemsPerRow={2}>
          <Button
            testID="personal-data.request-access"
            palette="secondary"
            onPress={handleAccessRequest}
          >
            {formatMessage(messages.personalDataAccessCta)}
          </Button>
          <Button
            testID="personal-data.request-delete"
            palette="secondary"
            onPress={handleDeleteRequest}
          >
            {formatMessage(messages.personalDataDeleteCta)}
          </Button>
        </Stack>
      </View>
    </ScrollViewWithHeaderTitle>
  );
};

// ─── Navigation Hooks ───────────────────────────────────────────────

const usePersonalDataRequestNavigation = () => {
  const router = useRouter();

  const handleAccessRequest = useCallback(() => {
    router.navigate({
      pathname: '/personal-data-details',
      params: { action: 'access' },
    });
  }, [router]);

  const handleDeleteRequest = useCallback(() => {
    router.navigate({
      pathname: '/personal-data-details',
      params: { action: 'delete' },
    });
  }, [router]);

  return useMemo(
    () => ({ handleAccessRequest, handleDeleteRequest }),
    [handleAccessRequest, handleDeleteRequest],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly personalDataPrivacyPolicyDescription: NoMessageValues;
  readonly personalDataPrivacyPolicyHref: NoMessageValues;
  readonly personalDataEmailDescription: NoMessageValues;
  readonly personalDataEmailHref: NoMessageValues;
  readonly personalDataPhoneDescription: NoMessageValues;
  readonly personalDataPhoneHref: NoMessageValues;
  readonly personalDataOptOutDescription: NoMessageValues;
  readonly personalDataOptOutHref: NoMessageValues;
  readonly personalDataTitle: NoMessageValues;
  readonly personalDataBody: {
    readonly email: MessageValue;
    readonly opt_out: MessageValue;
    readonly phone: MessageValue;
    readonly privacy_policy: MessageValue;
  };
  readonly personalDataAccessCta: NoMessageValues;
  readonly personalDataDeleteCta: NoMessageValues;
}>({
  personalDataPrivacyPolicyDescription: {
    defaultMessage: 'privacy policy',
    description: 'Personal Data | Privacy Policy | Description',
  },
  personalDataPrivacyPolicyHref: {
    defaultMessage: 'https://www.sweetgreen.com/privacy-policy',
    description: 'Personal Data | Privacy Policy | Href',
  },
  personalDataEmailDescription: {
    defaultMessage: 'hello@sweetgreen.com',
    description: 'Personal Data | Email | Description',
  },
  personalDataEmailHref: {
    defaultMessage: 'mailto:hello@sweetgreen.com',
    description: 'Personal Data | Email | Href',
  },
  personalDataPhoneDescription: {
    defaultMessage: '(833) 309-9300',
    description: 'Personal Data | Phone | Description',
  },
  personalDataPhoneHref: {
    defaultMessage: 'tel:+18333099300',
    description: 'Personal Data | Phone | Href',
  },
  personalDataOptOutDescription: {
    defaultMessage: 'https://www.sweetgreen.com/privacy-policy/ca-data-opt-out',
    description: 'Personal Data | Opt Out | Description',
  },
  personalDataOptOutHref: {
    defaultMessage: 'https://www.sweetgreen.com/privacy-policy/ca-data-opt-out',
    description: 'Personal Data | Opt Out | Href',
  },
  personalDataTitle: {
    defaultMessage: 'Sweetgreen Data Subject Requests',
    description: 'Personal Data | Title',
  },
  personalDataBody: {
    defaultMessage: `Thank you for visiting our webform where residents of California, Virginia, and other states where privacy laws applicable to us grant their residents certain rights can submit requests regarding their personal information. Visit our {privacy_policy} for more information on these rights.

Please choose from the requests below and click Submit (if needed) to submit your request. Please note that if you request that sweetgreen delete your data, sweetgreen will need to delete your sweetgreen account entirely and that we will be unable to provide any online or digital services to you or support for past products or services that you have purchased.

If you would like to opt out of certain data sharing by sweetgreen, please visit us at {opt_out}.

If you would like to request information on how we handle your personal information, request that we correct inaccurate personal information or if you have any other questions about submitting requests, please call us at {phone}, or email us at {email}.`,
    description: 'Personal Data | Body',
  },
  personalDataAccessCta: {
    defaultMessage: 'Request to access your data',
    description: 'Personal Data | Access Cta',
  },
  personalDataDeleteCta: {
    defaultMessage: 'Delete your data & account',
    description: 'Personal Data | Delete Cta',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  contentLG: {
    width: '80%',
    maxWidth: 560,
    marginVertical: theme.spacing['20'],
    marginHorizontal: 'auto',
  },
  contentSM: {
    width: '90%',
    maxWidth: 560,
    marginVertical: theme.spacing['6'],
    marginHorizontal: 'auto',
  },
  titleLG: {
    marginBottom: theme.spacing['6'],
    textAlign: 'center',
  },
  titleSM: {
    marginBottom: theme.spacing['6'],
  },
  buttons: {
    marginTop: theme.spacing['6'],
  },
});
